import { isUsableFileNode } from '@/utils/fileStatus';
import { request } from '@umijs/max';

const isFolderType = (type: string) => {
  // If type is not 'file', treat it as a folder/container
  return type !== 'file';
};

const ALWAYS_SHOW_TYPES = [
  'personal',
  'private_group',
  'firm_wide',
  'public',
  'third_party',
  'group',
];

export const getFileLazyTreeView = async () => {
  // Fetch root files/folders using /api/files/
  const response = await request(`/api/files/`, {
    params: {
      page: 1,
      pageSize: 1000,
    },
  });

  // Handle response structure { data: { files: [...] } }
  const filesList = response?.data?.files || response?.files || [];

  // Map files/groups to FileTreeNode format
  const mappedFiles = filesList.map((file: any) => {
    const isFolder = isFolderType(file.type);
    // Root folders and groups don't have hasChildren attribute
    // Only use hasChildren if explicitly provided by API
    const hasChildren = file.hasChildren !== undefined ? file.hasChildren : undefined;

    return {
      key: file.id,
      title: file.name,
      is_folder: isFolder,
      hasChildren: hasChildren,
      isLeaf: !isFolder,
      group_id: file.group_id,
      ...file,
    };
  });

  // Check which root folders have groups by fetching their children
  const enrichedFiles = await Promise.all(
    mappedFiles.map(async (file: any) => {
      // For non-personal root folders, check if they have groups
      if (ALWAYS_SHOW_TYPES.includes(file.type) && file.type !== 'personal') {
        try {
          const childrenResponse = await request(`/api/files/${file.key}`, {
            params: { page: 1, pageSize: 1 },
          });
          const children = childrenResponse?.data?.files || childrenResponse?.files || [];

          // If no groups, disable this root folder
          if (children.length === 0) {
            return {
              ...file,
              disableCheckbox: true,
              selectable: false,
              disabled: true,
            };
          }
        } catch (error) {
          console.warn(`Failed to check children for ${file.title}`, error);
        }
      }
      return file;
    }),
  );

  // Filter out empty folders using hasChildren from API, except for special types
  const filteredFiles = enrichedFiles.filter((file: any) => {
    if (!isUsableFileNode(file)) {
      return false;
    }
    if (file.is_folder) {
      // Root folders and groups don't have hasChildren - always show them
      if (ALWAYS_SHOW_TYPES.includes(file.type)) {
        return true;
      }
      // For other folders, only filter if hasChildren is explicitly false
      if (file.hasChildren === false) {
        return false;
      }
    }
    return true;
  });

  return { data: filteredFiles };
};

export const getNodeChildren = async (nodeKey: string) => {
  // Fetch files in the folder using /api/files/${nodeKey}
  const response = await request(`/api/files/${nodeKey}`, {
    params: {
      page: 1,
      pageSize: 1000,
    },
  });

  // Handle response structure { data: { files: [...] } }
  const filesList = response?.data?.files || response?.files || [];

  const mappedFiles = filesList.map((file: any) => {
    const isFolder = isFolderType(file.type);
    // Only use hasChildren if explicitly provided by API
    const hasChildren = file.hasChildren !== undefined ? file.hasChildren : undefined;

    return {
      key: file.id,
      title: file.name,
      is_folder: isFolder,
      hasChildren: hasChildren,
      isLeaf: !isFolder,
      ...file,
    };
  });

  return { data: mappedFiles.filter(isUsableFileNode) };
};

export const getFileDetail = async (id: string) => {
  return request(`/api/files/${id}`);
};

export const getFileContext = async () => {
  return request(`/api/chat/context`);
};

export const getFileContextById = async (contextId: string) => {
  return request(`/api/chat/context/${contextId}`);
};

export const updateContext = async (contextId: string, data: any) => {
  return request(`/api/chat/context/${contextId}`, { method: 'PUT', data });
};

export const addContext = async (data: any) => {
  return request(`/api/chat/context`, { method: 'POST', data });
};
